/******* Angular Resourse *******/
import { Component, OnInit, Input, Output, ViewChild, ElementRef, SimpleChanges, EventEmitter, OnChanges, AfterViewInit, OnDestroy } from '@angular/core';

/******* Type Resourse *******/
import { OverlayMaps, MapIcons, Popup, OverlayMapsType, MapType, BaseMapType, SystemPlatform } from '@core/types';

/******* Plug-In Resourse *******/
import * as L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.fullscreen';
import * as _ from 'lodash';

/******* Environment Resourse *******/
import { UtilsService } from '@core/services/utils.service';
import { fadeSlideIn } from '@core/animations/core.animation';

/******* Const Resourse *******/
const GEOAPIFY_API_KEY: string = 'd2981e8678e845fda45633d22373783d';
const CELL_CLUSTER_GROUP_OPTIONS = {
    iconCreateFunction: function (cluster) {
        const number = cluster.getChildCount();
        let className = "cluster";
        let iconSize;
        let iconAnchor = [30, 40];
        if (number < 10) {
            className += " cluster-m1";
            iconSize = L.point(60, 60);
            iconAnchor = [30, 40];
        }
        else if (number < 100) {
            className += " cluster-m2";
            iconSize = L.point(60, 60);
            iconAnchor = [30, 40];
        }
        else if (number < 500) {
            className += " cluster-m3";
            iconSize = L.point(70, 70);
            iconAnchor = [30, 45];
        }
        else if (number < 1000) {
            className += " cluster-m4";
            iconSize = L.point(70, 70);
            iconAnchor = [30, 45];
        }
        else {
            className += " cluster-m5";
            iconSize = L.point(90, 90);
            iconAnchor = [30, 60];
        }

        return L.divIcon({
            html: number,
            className,
            iconSize,
            iconAnchor: L.point(iconAnchor as [number, number])
        });
    },
    spiderfyOnMaxZoom: false,
    zoomToBoundsOnClick: false
};

@Component({
    selector: 'geoapify-map',
    animations: [fadeSlideIn],
    templateUrl: './core-map.component.html',
    styleUrls: ['core-map.component.scss']
})
export class MapComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    /******* Element Resourse *******/

    // Input data

    // Location
    @Input() Latitude: number = 0;
    @Input() Longitude: number = 0;

    // Zoom
    @Input() Zoom: number = 15;

    // With Map Attribution
    @Input() withMapAttribution: boolean = false;

    // With Layers Switcher
    @Input() withLayerSwitcher: boolean = false;

    // With Marker Cluster
    @Input() withMarkerCluster: boolean = true;

    // With Cell Cluster
    @Input('withCellCluster') withCellCluster: boolean = true;

    // With Device
    @Input() withDeviceDisplay: boolean = false;

    // Drawing completed
    @Output('drawingResults') drawingResults: EventEmitter<Array<L.LatLng>> = new EventEmitter<Array<L.LatLng>>();

    // Map Container

    // Leaflet map instance
    private leafletMap: L.Map | null = null;

    // Layer Control
    private layerControl: L.Control.Layers;

    // OverlayMaps
    private overlayMaps: Partial<OverlayMaps>;

    // Map Element
    @ViewChild('map') private mapContainer: ElementRef<HTMLElement>;

    // Map Type
    private readonly mapType: MapType = {
        [BaseMapType.BRIGHT]: `https://maps.geoapify.com/v1/tile/osm-bright/{z}/{x}/{y}.png?apiKey=${GEOAPIFY_API_KEY}`,
        [BaseMapType.BRIGHT_SMOOTH]: `https://maps.geoapify.com/v1/tile/osm-bright-smooth/{z}/{x}/{y}.png?apiKey=${GEOAPIFY_API_KEY}`,
        [BaseMapType.DARK]: `https://maps.geoapify.com/v1/tile/dark-matter-purple-roads/{z}/{x}/{y}.png?apiKey=${GEOAPIFY_API_KEY}`,
        [BaseMapType.LIBERTY]: `https://maps.geoapify.com/v1/tile/osm-liberty/{z}/{x}/{y}.png?apiKey=${GEOAPIFY_API_KEY}`,
        [BaseMapType.SATELLITE]: `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}`,
        [BaseMapType.OFFLINE_MAP]: `/tiles/styles/basic-preview/512/{z}/{x}/{y}.png`,
        [BaseMapType.OPEN_STREET_MAP]: `https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png`
    };

    // Drawing Mode
    private _drawingMode: boolean = false;
    private _drawingPoints: Array<L.LatLng> = [];

    /**
     * constructor
     * 
     * @param {ElementRef} el
     * @param {ElementRef} _utilsService
     */
    constructor(private el: ElementRef,
        private _utilsService: UtilsService) { }

    //  Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter : access Map Zoom
     */
    public get mapZoom() {
        return this.leafletMap ? this.leafletMap.getZoom() : 0;
    }

    /**
     * Get the map instance
     */
    get map() {
        return this.leafletMap ? this.leafletMap : null;
    }

    /**
     * is drawing mode
     */
    get isDrawingMode() {
        return this._drawingMode;
    }

    /**
     * get current Bounds
     */
    get Bounds() {
        return this.leafletMap ? this.leafletMap.getBounds() : null;
    }

    // Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit() {

        // initial Overlay Map
        this.overlayMaps = {
            [OverlayMapsType.MARKERS]: this.withMarkerCluster ? L.markerClusterGroup({
                spiderfyOnMaxZoom: false,
                zoomToBoundsOnClick: false
            }) : L.layerGroup(),
            [OverlayMapsType.CELL_MARKERS]: this.withCellCluster ? L.markerClusterGroup(CELL_CLUSTER_GROUP_OPTIONS) : L.layerGroup(),
            [OverlayMapsType.CELL_MARKERS_HISTORY]: this.withCellCluster ? L.markerClusterGroup(CELL_CLUSTER_GROUP_OPTIONS) : L.layerGroup(),
            [OverlayMapsType.KML_POLY]: L.layerGroup(),
            [OverlayMapsType.KML_CELL_MARKERS]: this.withCellCluster ? L.markerClusterGroup(CELL_CLUSTER_GROUP_OPTIONS) : L.layerGroup()
        };

        if (this._utilsService.platformConfig.geofenceEnable) {
            this.overlayMaps[OverlayMapsType.DRAWING] = L.layerGroup();
            this.overlayMaps[OverlayMapsType.GEOFENCE] = L.layerGroup();
        }
    }

    ngOnChanges(changes: SimpleChanges) {

        // detect change
        if (changes.Latitude || changes.Longitude || changes.Zoom) {
            if (this.leafletMap) this.setView(this.Latitude, this.Longitude, this.Zoom);
        }
    }

    ngAfterViewInit() {
        this._initialMap();
    }

    ngOnDestroy(): void {
        if (this.leafletMap) {
            this.leafletMap.off();
            this.leafletMap.remove();
            this.leafletMap = null;
        }
        // clear overlay maps
        for (let key in this.overlayMaps) {
            if (this.overlayMaps[key] && (this.overlayMaps[key] as any).clearLayers) {
                (this.overlayMaps[key] as any).clearLayers();
            }
        }
    }

    // Private Methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get Base Map Options
     * 
     * @param baseMaps
     */
    private _getBaseMapOptions(baseMaps: any): any {
        if (this.withLayerSwitcher) {
            return _.pick(baseMaps, this._utilsService.platformConfig.baseMapOptions);
        }
        else {
            if (this._utilsService.platformConfig.simpleBaseMapOptions.length === 0) this._removeLayerControl();
            return _.pick(baseMaps, this._utilsService.platformConfig.simpleBaseMapOptions);
        }
    }

    /**
     * Remove Layer Control
     */
    private _removeLayerControl() {
        this._utilsService.runAfterAnotherFrame(() => {
            const ele = this.el.nativeElement.getElementsByClassName('leaflet-control-layers');
            if (ele && !this.withLayerSwitcher) for (let index = 0; index < ele.length; index++) (<HTMLElement>ele[index]).style.display = 'none'; // Hide layer switcher
        });
    }

    /**
     * initial Map
     */
    private _initialMap(): void {

        // Create Base Layer Maps Option
        const baseMaps = this._renderBaseMaps();

        // Map overlay options
        let overlayMapOptions = {
            'CPE': this.overlayMaps[OverlayMapsType.MARKERS],
            'Cell-Site': this.overlayMaps[OverlayMapsType.CELL_MARKERS],
            'KML-Cell-Site': this.overlayMaps[OverlayMapsType.KML_CELL_MARKERS],
            'KML-Poly': this.overlayMaps[OverlayMapsType.KML_POLY]
        };
        if (this._utilsService.platformConfig.geofenceEnable) overlayMapOptions['Geo-Fence'] = this.overlayMaps[OverlayMapsType.GEOFENCE];

        // Iniitial Base Map and option
        const initialBaseMap = baseMaps[this._utilsService.platformConfig.defaultBaseMapType];

        // Iniitial layers
        let layers = [
            initialBaseMap,
            this.overlayMaps[OverlayMapsType.MARKERS],
            this.overlayMaps[OverlayMapsType.CELL_MARKERS],
            this.overlayMaps[OverlayMapsType.CELL_MARKERS_HISTORY],
            this.overlayMaps[OverlayMapsType.KML_CELL_MARKERS],
            this.overlayMaps[OverlayMapsType.KML_POLY]
        ];
        if (this._utilsService.platformConfig.geofenceEnable) layers.push(this.overlayMaps[OverlayMapsType.GEOFENCE])

        // Initial the Map
        this.leafletMap = L.map(this.mapContainer.nativeElement, {
            center: [this.Latitude, this.Longitude],
            zoom: this.Zoom,
            layers, // set Liberty as default base
            fullscreenControl: true,
            fullscreenControlOptions: {
                position: 'topleft',
                title: 'Full Screen',
                titleCancel: 'Back',
                forcePseudoFullscreen: true, // limit fullscreen to window of map
            }
        });

        // Create layer control
        const markerOptions = this.withLayerSwitcher ? overlayMapOptions : {};
        let baseMapOptions = this._getBaseMapOptions(baseMaps);
        if (this.leafletMap) this.layerControl = L.control.layers(baseMapOptions, markerOptions, { hideSingleBase: true }).addTo(this.leafletMap);

        // Add Center Device Marker
        if (this.withDeviceDisplay) {
            this.clearLayerById(OverlayMapsType.MARKERS);
            this.addMarkerLayer("Device", MapIcons.DEVICE_GREY, this.Latitude, this.Longitude, OverlayMapsType.MARKERS);
        }

        // Remove Attribution
        if (!this.withMapAttribution) {
            this._utilsService.runAfterAnotherFrame(() => {
                const ele = this.el.nativeElement.getElementsByClassName('leaflet-control-attribution');
                if (ele) for (let index = 0; index < ele.length; index++) (<HTMLElement>ele[index]).style.display = 'none'; // Hide map attribute
            });
        }

        // Full Screen enter and leave call back
        if (this.leafletMap) {
            this.leafletMap.on('enterFullscreen', () => {
                this.fitBounds(50);
                this.closeAllPopup();
            });
            this.leafletMap.on('exitFullscreen', () => {
                this.fitBounds(50);
                this.closeAllPopup();
            });

            // Click Map
            if (this._utilsService.platformConfig.geofenceEnable) {
                this.leafletMap.on('click', (event) => {
                    if (this._drawingMode) this._handleDrawing(event);
                });
            }
        }

        // cluster click
        if (this.withMarkerCluster) {
            this.overlayMaps[OverlayMapsType.MARKERS].on('clusterclick', (a) => this._handleClusterClick(a));
            this.overlayMaps[OverlayMapsType.CELL_MARKERS].on('clusterclick', (a) => this._handleClusterClick(a));
            this.overlayMaps[OverlayMapsType.KML_CELL_MARKERS].on('clusterclick', (a) => this._handleClusterClick(a));
        }
    }

    /**
     * Handle Cluster Click
     * @param a 
     */
    private _handleClusterClick(a: any): void {
        const cluster = a.layer;
        let bottomCluster = cluster;
        while (bottomCluster._childClusters.length === 1)
            bottomCluster = bottomCluster._childClusters[0];

        if (bottomCluster._zoom === this.overlayMaps[OverlayMapsType.CELL_MARKERS]['_maxZoom'] &&
            bottomCluster._childCount === cluster._childCount) {
            if (this.leafletMap) this.leafletMap.panTo(cluster._latlng);
            setTimeout(() => {
                const allMarkers = cluster.getAllChildMarkers();
                const content = allMarkers.map(marker => marker._popup._content);
                const template = `<div class="cluster-markers-container">${content.join(`<hr/>`)}</div>`;
                cluster.bindPopup(template).openPopup();
            }, 400);
        }
        else cluster.zoomToBounds({ padding: [20, 20] });
    }

    /**
     * handle drawing event
     * 
     * @param event 
     */
    private _handleDrawing(event): void {
        switch (event.type) {
            case 'click':
                if (this._drawingPoints.length > 0)
                    this._drawLineInDrawing([this._drawingPoints[this._drawingPoints.length - 1], event.latlng]);
                this._drawPointInDrawing(event.latlng);
                break;
            default:
                break;
        };
    }

    /**
     * draw point in drawing 
     * 
     * @param {LatLng} latLng 
     */
    private _drawPointInDrawing(latLng: L.LatLng): void {
        const cutomizeIcon: L.IconOptions = {
            iconUrl: `assets/images/map/map_cursor.svg`,
            iconSize: [19, 19],
            popupAnchor: [0, 10],
            iconAnchor: [10, 10],
            tooltipAnchor: [10, 0]
        };
        let icon = L.icon(cutomizeIcon);
        let markerTemplate = L.marker([latLng.lat, latLng.lng], {
            icon: icon,
            zIndexOffset: 3,
            opacity: 0.6
        });
        markerTemplate.on('click', (event: any) => {
            const initialPoint = this._drawingPoints[0];
            if (initialPoint.lat === latLng.lat && initialPoint.lng === latLng.lng && this._drawingPoints.length > 1) {
                // finish the geo fence
                const polygonPoints = [...this._drawingPoints];
                this.drawingResults.emit(polygonPoints); // emit results

                // clear drawing
                this._drawingPoints = [];
                this.clearLayerById(OverlayMapsType.DRAWING);
            }
        });
        markerTemplate.on('mouseover', function (e) {
            markerTemplate.setOpacity(1);
        });
        markerTemplate.on('mouseout', function (e) {
            markerTemplate.setOpacity(0.6);
        });
        markerTemplate.bindTooltip(`Point: ${latLng.lat}, ${latLng.lng}`);
        this.overlayMaps[OverlayMapsType.DRAWING].addLayer(markerTemplate);
        this._drawingPoints.push(latLng);
    }

    /**
     * draw line in drawing 
     * 
     * @param {Array<L.LatLng>} latLngs 
     */
    private _drawLineInDrawing(latLngs: Array<L.LatLng>): void {
        let polyLineTemplate = L.polyline(latLngs, {
            color: "#7367f0",
            weight: 3,
            opacity: 1
        });
        this.overlayMaps[OverlayMapsType.DRAWING].addLayer(polyLineTemplate);
    }

    /**
     * draw polygon in drawing
     * 
     * @param {Array<L.LatLng>} latLngs 
     * @param {string} name
     * @param {string} id
     */
    private _drawPolygonInGeoFence(latLngs: Array<L.LatLng>, name: string): any {
        let polygonTemplate = L.polygon(latLngs, {
            color: "#7367f0",
            weight: 1,
            opacity: 1,
            fillColor: "#7367f0",
            fillOpacity: 0.2
        });
        if (name) polygonTemplate.bindTooltip(name);
        this.overlayMaps[OverlayMapsType.GEOFENCE].addLayer(polygonTemplate);
        return polygonTemplate;
    }

    /**
     * Render Base Layer Map
     * 
     * @returns 
     */
    private _renderBaseMaps(): L.Control.LayersObject {
        let baseMaps = {};
        for (let key in this.mapType) {
            switch (key) {
                case BaseMapType.SATELLITE:
                    baseMaps[key] = L.tileLayer(this.mapType[key], {
                        maxZoom: 16,
                        id: key + "_id",
                        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
                        noWrap: true
                    });
                    break;
                default:
                    baseMaps[key] = L.tileLayer(this.mapType[key], {
                        maxZoom: 20,
                        id: key + "_id",
                        attribution: '© OpenStreetMap',
                        noWrap: true
                    });
                    break;
            }
        }
        return baseMaps;
    }

    /**
     * Get build in maps marker
     * 
     * @param {string} markerName
     */
    private buildInMapsMarker(markerName: string): L.IconOptions {
        return {
            iconUrl: `assets/images/map/${markerName}.png`,
            iconSize: [48, 48],
            iconAnchor: [24, 45],
            tooltipAnchor: [18, -24],
            popupAnchor: [0, -45]
        };
    }

    // Public Methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * add Marker Layer into Marker Group
     * 
     * @param {string} id 
     * @param {MapIcons | string} iconType 
     * @param {number} latitude 
     * @param {number} longitude 
     * @param {string} tootip 
     * @param {Popup} popup 
     */
    public addMarkerLayer(id: string,
        iconType: MapIcons | string,
        latitude: number,
        longitude: number,
        layer: OverlayMapsType,
        tootip: string = "",
        popup: Popup = {
            content: "",
            options: {}
        }): void {

        // Customize Icon
        let icon;
        iconType = iconType ? iconType : MapIcons.CELL;
        if (!Object.keys(MapIcons).map(x => MapIcons[x]).includes(iconType)) {
            const cutomizeIcon: L.IconOptions = {
                iconUrl: iconType,
                iconSize: [30, 30],
                popupAnchor: [0, -7.5],
                iconAnchor: [15, 15],
                tooltipAnchor: [15, 0]
            };
            icon = L.icon(cutomizeIcon);
        } else icon = L.icon(this.buildInMapsMarker(iconType));

        // Validate Coordinate

        let markerTemplate = L.marker([latitude, longitude], {
            icon: icon,
            zIndexOffset: 3,
            title: id
        });

        if (tootip) markerTemplate.bindTooltip(tootip);
        if (popup.content) markerTemplate.bindPopup(popup.content, popup.options);

        // Add to Marker Layer
        this.overlayMaps[layer].addLayer(markerTemplate);
        // if (iconType === MapIcons.DEVICE || iconType === MapIcons.DEVICE_GREY) this.overlayMaps.markers.addLayer(markerTemplate);
        // else this.overlayMaps.cellMarkers.addLayer(markerTemplate);
    }

    /**
     * Drawing Line
     * 
     * @param polyline_ [[Latitude,Longtitude],[Latitude,Longtitude]]
     * @param tootip 
     * @param popup
     * @returns 
     */
    public drawLine(polyline_: any,
        tootip: string = "",
        popup: string = "",
        options: any = {
            color: "#ea5455",
            weight: 2,
            opacity: 0.7
        }): boolean | void {

        let polyLineTemplate = L.polyline(polyline_, {
            ...options
        });

        if (tootip) polyLineTemplate.bindTooltip(tootip);
        if (popup) polyLineTemplate.bindPopup(popup);

        // Add to Marker Layer
        this.overlayMaps[OverlayMapsType.KML_POLY].addLayer(polyLineTemplate);
    }

    /**
     * Drawing Polygon
     * 
     * @param {Array<L.Latlng>} polygon_ 
     * @param {string} tootip 
     * @param {string} popup 
     * @param {string} color 
     * @param {number} weight 
     * @param {number} opacity 
     * @param {string} fillColor 
     * @param {number} fillOpacity 
     * @returns {boolean | void}
     */
    public drawPolygon(polygon_: Array<L.LatLng>,
        tootip: string = "",
        popup: string = "",
        color: string = "#ea5455",
        weight: number = 2,
        opacity: number = 0.7,
        fillColor: string = "#7367f0",
        fillOpacity: number = 0.2): boolean | void {

        let polygonTemplate = L.polygon(polygon_, {
            color: color ? color : "#ea5455",
            weight: weight ? weight : 2,
            opacity: opacity ? opacity : 0.7,
            fillColor: fillColor ? fillColor : "#7367f0",
            fillOpacity: fillOpacity ? fillOpacity : 0.2
        });

        if (tootip) polygonTemplate.bindTooltip(tootip);
        if (popup) polygonTemplate.bindPopup(popup);

        // Add to Marker Layer
        this.overlayMaps[OverlayMapsType.KML_POLY].addLayer(polygonTemplate);
    }

    /**
     * Drawing circle
     * 
     * @param {L.Latlng} latLng 
     * @param {number} radius
     * @param {any} options // #circle-option
     * @returns {boolean | void}
     */
    public drawCircle(latLng: L.LatLng,
        radius: number,
        options?: any): boolean | void {
        let circleTemplate = L.circle(latLng, radius, options);
        this.overlayMaps[OverlayMapsType.KML_POLY].addLayer(circleTemplate); // Add to Marker Layer
    }

    /**
     * Update Popup content by id
     * 
     * @param {string} id 
     * @param {string} updateContent 
     */
    public updatePopupFromMarker(id: string, updateContent: string, fromOverlay: OverlayMapsType = OverlayMapsType.MARKERS): void {
        this.overlayMaps[fromOverlay].eachLayer((layer: L.Marker) => {
            if (layer.options.title === id) {
                layer.setPopupContent(updateContent);
            };
        });
    }

    /**
     * Fit Bounds
     * 
     * @param {number} paddingValue
     */
    public fitBounds(paddingValue: number): void {
        if (!this.leafletMap) return;

        // Bounds Aarray
        let boundArray: Array<L.LatLng> = [];

        // Marker Bounds
        this.overlayMaps[OverlayMapsType.MARKERS].eachLayer((layer: L.Marker) => {
            boundArray.push(layer.getLatLng());
        });

        this.overlayMaps[OverlayMapsType.CELL_MARKERS].eachLayer((layer: L.Marker) => {
            boundArray.push(layer.getLatLng());
        });

        this.overlayMaps[OverlayMapsType.KML_CELL_MARKERS].eachLayer((layer: L.Marker) => {
            boundArray.push(layer.getLatLng());
        });

        // Calculate Bounds
        let bounds = L.latLngBounds(boundArray);
        let polyFlag = false;

        // Polyline Bounds
        this.overlayMaps[OverlayMapsType.KML_POLY].eachLayer((layer: L.Polyline) => {
            bounds.extend(layer.getBounds());
            polyFlag = true;
        });

        // Geo Fence Bounds
        if (this._utilsService.platformConfig.geofenceEnable) {
            this.overlayMaps[OverlayMapsType.GEOFENCE].eachLayer((layer: L.Polygon) => {
                bounds.extend(layer.getBounds());
            });
        }

        if (boundArray.length > 0 && this.leafletMap)
            this.leafletMap.fitBounds(bounds, { padding: [paddingValue + 20, paddingValue + 20] }); // Fit Bounds with padding

        if (!polyFlag && boundArray.length == 1 && this.leafletMap) this.leafletMap.setView(boundArray[0], 13);
    }

    /**
     * Fit Single Bounds
     * 
     * @param {Array<L.LatLng>} latLngs
     * @param {number} paddingValue
     */
    public fitSingleBounds(latLngs: Array<L.LatLng>, paddingValue: number): void {
        if (!this.leafletMap || latLngs.length === 0) return;
        this.leafletMap.fitBounds(L.latLngBounds(latLngs), { padding: [paddingValue + 20, paddingValue + 20] });
    }

    /**
     * Clear the Map Object
     */
    public clearMap(): void {
        // Clear Marker Layer
        for (let laygroup in this.overlayMaps) {
            this.overlayMaps[laygroup].clearLayers();
        }
    }

    /**
     * Move the Map View
     * 
     * @param lat 
     * @param long 
     * @param zoom 
     * @returns 
     */
    public setView(lat, long, zoom): boolean | void {
        if (!this.leafletMap) return false;
        if (lat > 90.0 || lat < -90.0 || long > 180.0 || long < -180.0 || zoom > 20 || zoom < 1) {
            console.warn("SetView with incorrect Value");
            return false;
        }
        this.leafletMap.setView([lat, long], zoom);
    }

    /**
     * Resize the Map
     */
    public invalidateSize(): void {
        if (this.leafletMap) this.leafletMap.invalidateSize();
    }

    /**
     * Clear assigned layed
     * 
     * @param {string} id
     */
    public clearLayerById(id): void {
        const layer = this.overlayMaps[id];
        if (layer && (layer as any).clearLayers) (layer as any).clearLayers();
    }

    /**
     * Close all popup in marker
     */
    public closeAllPopup(): void {
        const layers = [OverlayMapsType.MARKERS, OverlayMapsType.CELL_MARKERS];
        layers.forEach(type => {
            const g = this.overlayMaps[type];
            if (g && (g as any).eachLayer) {
                (g as any).eachLayer((layer: any) => {
                    if (layer && layer.closePopup) layer.closePopup();
                });
            }
        });
    }

    /**
     * Toggle drawing mode
     */
    public toggleDrawingMode(): void {
        this._drawingMode = !this._drawingMode;

        // marker layer
        const markerLayers: Array<OverlayMapsType> = [
            OverlayMapsType.MARKERS,
            OverlayMapsType.CELL_MARKERS,
            OverlayMapsType.CELL_MARKERS_HISTORY,
            OverlayMapsType.KML_CELL_MARKERS,
            OverlayMapsType.KML_POLY
        ];
        if (this._drawingMode) {
            this._drawingPoints = []; // initial point set
            this.clearLayerById(OverlayMapsType.DRAWING);
            markerLayers.forEach((layerKey) => {
                if (this.leafletMap && this.leafletMap.hasLayer(this.overlayMaps[layerKey]))
                    this.leafletMap.removeLayer(this.overlayMaps[layerKey]);
            });
            if (this.leafletMap) this.leafletMap.addLayer(this.overlayMaps[OverlayMapsType.DRAWING]);
            if (this.leafletMap && !this.leafletMap.hasLayer(this.overlayMaps[OverlayMapsType.GEOFENCE]))
                this.leafletMap.addLayer(this.overlayMaps[OverlayMapsType.GEOFENCE]);
        }
        else {
            markerLayers.forEach((layerKey) => {
                if (this.leafletMap && !this.leafletMap.hasLayer(this.overlayMaps[layerKey]))
                    this.leafletMap.addLayer(this.overlayMaps[layerKey]);
            });
            if (this.leafletMap) this.leafletMap.removeLayer(this.overlayMaps[OverlayMapsType.DRAWING]);
        }

        // Layer control
        let ele = this.el.nativeElement.getElementsByClassName('leaflet-control-layers');
        for (let index = 0; index < ele.length; index++)
            (<HTMLElement>ele[index]).style.visibility = this._drawingMode ? 'hidden' : 'visible';

    }

    /**
     * Adding Geo Fence
     * 
     * @param {string} name 
     * @param {Array<L.LatLng>} latLng 
     */
    public addGeoFence(name: string, latLng: Array<L.LatLng>): any {
        return this._drawPolygonInGeoFence(latLng, name);
    }

    /**
     * Toggle Visibility on layer
     * 
     * @param template 
     * @param {OverlayMapsType} type 
     */
    public toggleVisibility(template: any, type: OverlayMapsType): void {
        if (this.overlayMaps[type].hasLayer(template)) this.overlayMaps[type].removeLayer(template);
        else this.overlayMaps[type].addLayer(template);
    }

    /**
     * Revmoe layer
     * 
     * @param template 
     * @param {OverlayMapsType} type 
     */
    public removeLayer(template: any, type: OverlayMapsType): void {
        const layer = this.overlayMaps[type];
        if (layer && (layer as any).hasLayer && (layer as any).hasLayer(template))
            (layer as any).removeLayer(template);
    }

    /**
     * Cancel Drawing Mode
     */
    public cancelDrawingMode(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.toggleDrawingMode();
    }
}
